// Per-book configuration — the single source of truth for book metadata and
// source-format quirks. Shared by Convex functions, the ingest scripts, and
// src (hence its home under convex/model/, per the shared-code rule): one
// record per catalog book, checked in here rather than re-derived at each call site.
//
// Source-repo *locations* are NOT encoded as absolute paths: scripts take a
// `--source-root` (default ~/code/sara) and join it with `sourceDir` — so this
// module stays pure (no node imports) and portable to CI.

/**
 * Where a matter document sits in reading order.
 *
 *   `front` / `back` — before the book's first chapter, after its last. The
 *     whole-book positions, and the right answer for a single-volume book:
 *     spelling the afterword as `{ at: "after", chapter: 17 }` instead would
 *     silently strand it mid-book the day Peter Pan gained a chapter 18.
 *   `before` / `after` — immediately before or after a NAMED chapter. What a
 *     multi-volume book needs: the Odyssey's volume-one afterword belongs
 *     after chapter 4 and before chapter 5, a position no whole-book placement
 *     can express.
 *
 * Anchored to a CHAPTER, deliberately not to a volume id. `src/reader/shelf.ts`
 * owns the fact that a book ships as more than one physical cover and states
 * plainly that neither the catalog nor the build pipeline knows it; a
 * volume-id anchor would drag that knowledge into the catalog schema, this
 * config, and the corpus build all at once. A chapter number is something both
 * sides already agree on, so the shelf can keep deriving volume membership
 * from the anchor without the anchor ever mentioning a volume.
 */
export type MatterPlacement =
  | { readonly at: "front" }
  | { readonly at: "back" }
  | { readonly at: "before"; readonly chapter: number }
  | { readonly at: "after"; readonly chapter: number };

/**
 * One loose front/back matter file (foreword / afterword / about-the-author).
 * Matter is single-column reader content: it has no original counterpart, so
 * it gets no pairing number, no sync anchors, and no translation popover
 * (PD-2). `slug` is the catalog identity and the `/read/:book/:slug` route
 * segment — it must never be all-digits, which is how the reader route
 * distinguishes a matter slug from a chapter number.
 *
 * The FILE is the seed input only. Convex owns a matter document's text once
 * it has been seeded (`matterMaster`), exactly as it owns a chapter's, so this
 * path is read by `scripts/seed.ts` and by a `--local` corpus build — never by
 * a production one.
 */
export interface MatterConfig {
  /**
   * Path to the source file, relative to the book source root
   * (e.g. "foreword.md", or "rewrites/vol_1_epilogue.md" for a file that lives
   * beside the chapter rewrites rather than at the top).
   */
  file: string;
  /** Catalog + route slug (kebab-case, non-numeric). */
  slug: string;
  /** Reading-order placement — see {@link MatterPlacement}. */
  placement: MatterPlacement;
}

/** Original-source format quirks handled by the ingest parser (C4). */
export interface OriginalFormat {
  /**
   * peter_pan emits a doubled header: `# Chapter I: TITLE` immediately followed
   * by a duplicate `## TITLE`. Keep the level-1 heading and drop the level-2
   * duplicate (SPEC "Build & ingest pipeline").
   */
  doubledHeaders: boolean;
  /**
   * Display headers use roman numerals (`# Chapter I: …`). The chapter number
   * is taken from the zero-padded arabic *filename*, never parsed from the
   * header text.
   */
  romanNumeralDisplayTitles: boolean;
  /** Strip `![…](../images/iNNN.jpg)` illustration refs (v1). */
  stripImageRefs: boolean;
  /**
   * Strip Project Gutenberg page-break artifacts — a bracketed page number
   * left inline in the prose (`Der Obergerichtsrat[250]  Droßelmeier war…`).
   *
   * Set per book because it is a property of the SCAN, not of the language or
   * the era: only nutcracker's source carries them (69 of them, `[249]`–`[317]`,
   * uniformly `[NNN]` followed by whitespace and never used for anything else
   * in that text). Stripping unconditionally would be a standing invitation for
   * a future book that writes real bracketed numbers — a footnote marker, a
   * verse number — to have them silently eaten.
   *
   * Applied to the raw Markdown before it is parsed, so the flattened
   * `DocumentModel.text` never contains the artifact and the anchor offsets
   * computed against it (IMPLEMENTATION "Anchor offsets") stay consistent
   * between seed and corpus build, which share this parser.
   */
  stripPageMarkers: boolean;
  /**
   * The original is continuous verse — one metrical line per source line, no
   * blank line between lines (Homer's rhapsodies). When set, the ingest parser
   * splits the poem's single mdast paragraph into per-line `verse-line` blocks
   * (a post-flatten span overlay; `text` stays byte-identical) so the reader
   * renders real line breaks and scroll-sync gets per-line geometry instead of
   * one tall block. Distinct from `verseChunking`, an align-pipeline concern:
   * this governs the ORIGINAL's rendered block structure. Set only for verse
   * books.
   */
  verseOriginal: boolean;
}

/**
 * Premium (paywalled) portion of a SELLABLE UNIT — the part sold as a one-time
 * purchase through checkout.sara.ai (plan 10). A sellable unit is the whole
 * book for a single-volume title and ONE VOLUME for a title priced per volume
 * (see {@link VolumeConfig}); its absence means that unit is wholly free.
 * Everything the paywall does is data-driven off this record — nothing is
 * hardcoded to a particular book. The `productId` is stable and hashed into
 * every cart, so it must never change once a purchase has been made against it
 * (renaming means a new product). `defaultAmountCents` is the fallback price;
 * the live price is the Convex env override (`CHECKOUT_PRICE_<PRODUCT_ID>`)
 * when set, so the displayed and attested amounts always share one source of
 * truth.
 */
export interface PremiumConfig {
  /**
   * The chapters that stay FREE within this unit. Every other chapter of the
   * unit is paywalled — so this one list is the whole free/paid boundary.
   *
   * A LIST rather than the old `fromChapter` cutoff because the boundary is not
   * always a contiguous tail, and because a book's free set (the union across
   * its volumes) has gaps: the Odyssey gives away volume one entire plus Book 5
   * and Book 13, the free opening samples of volumes two and three. Build it
   * with `freeChaptersThrough` where a rule exists rather than writing the
   * numbers out, so the RULE stays visible at the call site.
   *
   * EMPTY IS MEANINGFUL, and it is what an unseeded volume must use: a free
   * sample chapter is a promise that a specific chapter is readable, and
   * build-corpus hard-errors on a free chapter with no seeded retelling (only
   * PREMIUM chapters are skipped when un-retold). A volume configured ahead of
   * its content therefore paywalls all of itself until someone seeds it and
   * names its sample here — which fails closed, the safe direction.
   */
  freeChapters: readonly number[];
  /** Stable product id, hashed into every cart (`odyssey-premium`). */
  productId: string;
  /** Hosted-page + upsell display name for the product. */
  displayName: string;
  /** Fallback price in integer cents; env override is the real knob. */
  defaultAmountCents: number;
  /** ISO 4217 currency, lowercase (`usd`). */
  currency: string;
}

/**
 * How many opening chapters a SINGLE-volume book gives away: chapters 1–6 are
 * open, 7 onward are paid. One number rather than a per-book field because it
 * is a catalog-wide merchandising rule, not a property of any one title.
 */
export const SINGLE_VOLUME_FREE_CHAPTERS = 6;

/**
 * Fallback price for a book's premium tier, in integer cents — the standard
 * per-title list price, $8.00. Uniform across the catalog deliberately: it is
 * the rule, not a property of any one title.
 *
 * A FALLBACK, and now the third of four layers. An operator's `productPricing`
 * row wins over it (the console's Pricing tab), then the per-product env
 * override `CHECKOUT_PRICE_<PRODUCT_ID>`, then this, then
 * `DEFAULT_LIST_AMOUNT_CENTS` for a product no book configures at all — a
 * series. See `docs/ops/stats-and-pricing.md`.
 */
export const DEFAULT_PREMIUM_AMOUNT_CENTS = 800;

/** The free set for a single-volume book: chapters `1…n`. */
export function freeChaptersThrough(n: number): readonly number[] {
  return Array.from({ length: n }, (_, i) => i + 1);
}

/**
 * One physically-separate, separately-sellable volume of a multi-volume title.
 *
 * A VOLUME IS THE SELLABLE UNIT, not the book. Before this existed a book had
 * exactly one `productId`, so an entitlement minted from volume two's cover
 * also opened volume three — the same money bought strictly more than it was
 * offered. Each volume owning its own `PremiumConfig` is what makes that
 * impossible: the gate resolves a chapter to ITS volume's product id, and an
 * entitlement to another volume's id simply doesn't match.
 *
 * `chapters` is the inclusive range this volume binds, in the book's own
 * sequential numbering (Don Quixote's Book Two chapter N is sequential 52+N).
 * The ranges of a book's volumes must be ascending, contiguous, and
 * non-overlapping — that is what lets `volumeOfChapter` answer with a single
 * scan and no tie-break rule.
 *
 * This is the PAYWALL half of a volume only. What a volume is CALLED, what its
 * cover looks like, and where it sits on the shelf stay in `src/reader/shelf.ts`
 * — presentation the backend deliberately does not carry (and cannot: Convex
 * cannot import from `src/`). What moved here is the chapter BOUNDARY, because
 * money correctness has to be enforceable server-side.
 */
export interface VolumeConfig {
  /** 1-based, matching the shelf's "Volume One" / `book.yaml`'s `number`. */
  readonly volumeNumber: number;
  /** Inclusive chapter range, in the book's sequential numbering. */
  readonly chapters: { readonly from: number; readonly to: number };
  /** Absent = this volume is wholly free (the opening volume, by convention). */
  readonly premium?: PremiumConfig;
}

/**
 * Notation a book's RETELLING heading uses for its division number.
 *
 *   "arabic"   — `# Chapter 1: Peter Breaks Through` (peter_pan).
 *   "rhapsody" — the Homeric rhapsody letter, `# Book α: A Welcomed Guest`
 *                (α = Book 1 … ω = Book 24). This is what the odyssey's
 *                rewrites are actually titled with; the letter is the NUMBER,
 *                and the prose after the colon is the title.
 *
 * Arabic is accepted on PARSE under either setting, so a rewrite still carrying
 * the older `# Book 24: …` heading stays readable. It is not preserved on
 * SERIALIZE: the heading is regenerated from this setting (see `divisionLabel`),
 * so such a file is rewritten to `# Book ω: …` rather than echoed back.
 */
export type HeadingNumeral = "arabic" | "rhapsody";

/**
 * Hosted embedding model for the sync-anchor aligner (scripts/align/openai.ts).
 * Which model a book needs follows its language DISTANCE, so it is per-book
 * config, not a global default: on the ch1 ground-truth fixtures the true
 * window's cosine percentile at the true window size is 0.992 (large) vs 0.803
 * (small) for Homeric Greek, while German scores 0.999 under both. `large`
 * (3072 dims) earns its cost only on distant/ancient languages; English and
 * modern European languages take `small` (1536 dims).
 */
export type OpenAIEmbedModel =
  | "text-embedding-3-small"
  | "text-embedding-3-large";

/** Which embedding backend seeds a book's anchors — see `BookConfig.alignBackend`. */
export type AlignBackend = "gateway" | "openai";

/**
 * qwen3-embedding size served by embed.fugl.dev for English books.
 *
 * A CONSTANT, not per-book config: every English book is the same alignment
 * problem, so there is no case for varying it per title — and making it one
 * symbol keeps the A/B a one-line edit.
 *
 * Measured on peter-pan against blind ground truth (ch1 plus five held-out
 * spans), local qwen3-embedding:0.6b vs gateway 4B:
 *
 *              ch1 (training)   held-out spans
 *   0.6b       33/35  94%       43/44  98%
 *   4B         32/35  91%       40/44  91%
 *
 * 4B won nothing and lost three anchors, so `"0.6B"` is the default.
 *
 * Three anchors on 44 would be weak evidence on its own, so the disputed span
 * (ch13 ¶5-14) was ADJUDICATED: a second reader mapped it blind, seeing neither
 * the fixture nor either model's output. It confirmed the fixture on retell 10
 * (orig 7) and retell 11 (orig 8) — both of which 4B got wrong, one a
 * misplacement and one an anchor it declined to emit. The third, retell 14,
 * the reader independently placed differently again ([12] rather than the
 * fixture's [11,12] merge) and judged genuinely ambiguous, so it counts against
 * neither model. Two confirmed errors, not a scoring artefact.
 *
 * The mechanism is instructive: that span opens with a 1-to-3 fan-out (original
 * paragraph 3 split across retell 4/5/6, with material reordered inside it).
 * An aligner that mishandles the split arrives at retell 7 already offset and
 * stays wrong downstream — exactly the split-then-drift failure the
 * weakly-monotonic DP exists to prevent. 0.6b holds through it; 4B does not.
 *
 * Language distance is what buys accuracy (Greek 0.803 -> 0.992 on a bigger
 * model); English is distance zero and already saturated.
 */
export const GATEWAY_EMBED_SIZE: "0.6B" | "4B" = "0.6B";

/** One catalog book. */
export interface BookConfig {
  /** Catalog slug and the scope for deterministic block IDs (`bNNN`). */
  slug: string;
  /** Adaptation title (pinned; for odyssey this mirrors the source book.yaml). */
  title: string;
  /**
   * Original source language, lowercase code (source repo's `source.language`).
   * "en" disables the original-pane translation popover for the whole book.
   */
  sourceLanguage: string;
  /**
   * The same language written out ("Ancient Greek", "German"), used to LABEL
   * the committed anchor report's `lang` field. It reaches nothing else:
   * `align()` no longer takes a language at all, because the
   * translate-then-embed tier is deleted from the ladder and every backend
   * embeds its source language directly.
   *
   * Absent for English books, which report as "English" by default. A
   * non-English book that omits it is a hard error in seed.ts rather than a
   * report mis-labelled English — adding a book is a deliberate choice, and
   * this field is where it says so.
   */
  sourceLanguageName?: string;
  /** Book source folder, relative to `--source-root`. */
  sourceDir: string;
  /**
   * Chapter numbers emitted in v1 — the paired original ↔ retelling set. The
   * odyssey ships Books 1–12 only (12 of 24 rhapsodies retold).
   */
  chapters: number[];
  /**
   * The book's word for one of its divisions (`# Chapter N: …` for peter_pan,
   * `# Book N: …` for the odyssey). Two jobs: it builds the retelling heading
   * label for a book with no `divisionLabels` table, and it is the COLLECTIVE
   * noun the library uses either way ("28 chapters", "Chapters 5–12").
   */
  retellingHeadingNoun: string;
  /**
   * How the RETELLING heading writes the division number — the notation the
   * rewrite files actually use, which is NOT always arabic. See
   * `HeadingNumeral`; the parser accepts arabic in every book, so this only
   * says which OTHER notation is legal and which one gets rendered when the
   * Markdown is regenerated from the database.
   */
  retellingHeadingNumeral: HeadingNumeral;
  /**
   * Per-division heading labels, index = `chapterNumber - 1` — everything the
   * retelling heading puts before the colon. Set this ONLY for a book whose
   * divisions do not simply run `{retellingHeadingNoun} 1…N`.
   *
   * Frankenstein is why it exists: the source is Walton's four LETTERS followed
   * by Shelley's Chapters 1–24, so its sequential chapter 11 is the source's
   * "Chapter 7" and the noun itself changes at the seam. Pairing stays
   * sequential — chapter N of the original pairs with chapter N of the
   * retelling, as always — and this only changes what the reader is SHOWN, so
   * a reader can match a division against a print edition.
   *
   * `retellingHeadingNoun` is still required alongside it: that noun is the
   * COLLECTIVE word for the book's divisions ("28 chapters", "Previous
   * chapter"), which is a different job from naming one of them.
   */
  divisionLabels?: readonly string[];
  /** Heading noun in the ORIGINAL source files (every book so far uses "Chapter"). */
  originalHeadingNoun: string;
  /** Aligner embedding model when seeding against the hosted OpenAI backend —
   *  see {@link OpenAIEmbedModel} for why this tracks language distance. */
  openaiEmbedModel: OpenAIEmbedModel;
  /**
   * Which embedding backend seeds this book's anchors.
   *
   * Routed by LANGUAGE DISTANCE, which is the one variable every measurement
   * so far has tracked:
   *
   *   `"gateway"` — English. Self-hosted qwen3-embedding via embed.fugl.dev.
   *     English needs no translation and no frontier model: peter-pan scores
   *     98% on held-out spans with a small qwen. Keeping it self-hosted keeps
   *     the largest book off a metered API entirely.
   *   `"openai"` — everything else. A strong multilingual model is what let the
   *     translation tier be retired: German reads 0.999 true-window percentile
   *     on text-embedding-3-small, Homeric Greek 0.992 on -3-large (vs 0.803
   *     on -3-small), and heidi agrees with the old translate-then-embed path
   *     on 582 of 607 anchors while scoring >= it on every fixture.
   *
   * NOTHING routes to translategemma any more. Both backends embed the source
   * language directly and neither implements `translate`.
   */
  alignBackend: AlignBackend;
  original: OriginalFormat;
  /**
   * Verse chunking for continuous-verse originals (e.g. Homer). Consumed by
   * the seed pipeline (scripts/seed.ts), which reads this flag to decide
   * whether to chunk at all; `--verse-chunk-lines` only overrides the window
   * size once chunking is on.
   */
  verseChunking: boolean;
  /**
   * Loose front/back matter files (PD-2 resolved: matter ships in the catalog
   * as unnumbered single-column documents). Ingested by build-corpus with the
   * same parser as originals; order here is reading order within a placement.
   */
  matter: MatterConfig[];
  /**
   * Paywalled portion, for a SINGLE-VOLUME book (plan 10). Absent → either the
   * book is wholly free, or it is priced per volume and `volumes` carries the
   * paywall instead. Mutually exclusive with `volumes`; nothing reads this
   * field directly any more — ask `bookVolumes` (`model/volume.ts`), which
   * synthesizes one volume around it so every caller iterates volumes
   * uniformly.
   *
   * Every chapter NOT in `premium.freeChapters` is premium: its retelling text
   * is served only through the entitlement-gated Convex query, never emitted
   * into the public static corpus. The machinery stays dormant until a premium
   * chapter is actually seeded, so configuring chapters ahead of their rewrites
   * is safe (build-corpus + seed skip un-retold premium chapters).
   */
  premium?: PremiumConfig;
  /**
   * Present ONLY for a title priced per volume (odyssey, don-quixote today) —
   * see {@link VolumeConfig}. Absent = one sellable unit, exactly as before,
   * even when the shelf shows several covers (Frankenstein is six narrative
   * volumes upstream and one product here, deliberately).
   *
   * A TUPLE of at least two, so "a multi-volume title has more than one volume"
   * is a compile error rather than a runtime check. What the type cannot say is
   * that the ranges ascend contiguously; `volumeOfChapter` is written to answer
   * correctly regardless of order, and `volume.test.ts` pins the real configs.
   */
  volumes?: readonly [VolumeConfig, VolumeConfig, ...VolumeConfig[]];
  /**
   * MIGRATION-ONLY: the whole-title product id this book sold under BEFORE it
   * was priced per volume (`"odyssey-premium"`) — the id real `entitlements`
   * rows still carry.
   *
   * Reusing it as the id of the complete-title BUNDLE is what makes existing
   * purchasers need no data migration at all: their row is never touched, and
   * once this id resolves to a series over the volume ids (from the stored
   * `productPricing` row, or from `configLegacyBundleMembership()` before one
   * exists) the existing `seriesGranting` fan-out keeps satisfying every volume
   * for them. See `model/productCatalog.ts`.
   *
   * A title that goes multi-volume for the FIRST time after this change has no
   * legacy id and sets nothing here; its bundle is minted fresh in the pricing
   * panel.
   */
  legacyBundleProductId?: string;
}

const PETER_PAN: BookConfig = {
  slug: "peter-pan",
  title: "Peter Pan",
  sourceLanguage: "en",
  sourceDir: "peter_pan",
  chapters: Array.from({ length: 17 }, (_, i) => i + 1),
  retellingHeadingNoun: "Chapter",
  retellingHeadingNumeral: "arabic",
  originalHeadingNoun: "Chapter",
  openaiEmbedModel: "text-embedding-3-small",
  alignBackend: "gateway", // English — self-hosted qwen, no translation
  original: {
    doubledHeaders: true,
    romanNumeralDisplayTitles: true,
    stripImageRefs: true,
    stripPageMarkers: false,
    verseOriginal: false,
  },
  verseChunking: false,
  // Whole-book placements, not chapter anchors: Peter Pan ships as one volume,
  // so its afterword belongs after whatever the last chapter turns out to be.
  matter: [
    { file: "foreword.md", slug: "foreword", placement: { at: "front" } },
    { file: "afterword.md", slug: "afterword", placement: { at: "back" } },
    {
      file: "about_the_author.md",
      slug: "about-the-author",
      placement: { at: "back" },
    },
  ],
  // One cover, so the single-volume rule: chapters 1–6 open, 7–17 paid.
  premium: {
    freeChapters: freeChaptersThrough(SINGLE_VOLUME_FREE_CHAPTERS),
    productId: "peter-pan-premium",
    displayName: "Peter Pan — the complete retelling",
    defaultAmountCents: DEFAULT_PREMIUM_AMOUNT_CENTS,
    currency: "usd",
  },
};

const ODYSSEY: BookConfig = {
  slug: "odyssey",
  title: "The Odyssey",
  sourceLanguage: "grc",
  sourceLanguageName: "Ancient Greek",
  sourceDir: "rewrite-template/books/odyssey",
  // All 24 rhapsodies are configured; see `volumes` below for which are free.
  // A paid chapter only "activates" — appears in the catalog as a locked row —
  // once its retelling is actually seeded and published; until then
  // build-corpus and seed skip it, so configuring the full range is inert for
  // un-retold chapters. This is what makes the paywall data-driven rather than
  // hardcoded to a chapter count.
  chapters: Array.from({ length: 24 }, (_, i) => i + 1),
  retellingHeadingNoun: "Book",
  retellingHeadingNumeral: "rhapsody",
  originalHeadingNoun: "Chapter",
  openaiEmbedModel: "text-embedding-3-large",
  alignBackend: "openai", // Homeric Greek — large earns its cost here
  original: {
    doubledHeaders: false,
    romanNumeralDisplayTitles: false,
    stripImageRefs: false,
    stripPageMarkers: false,
    verseOriginal: true,
  },
  verseChunking: true,
  // PER-VOLUME matter, anchored to the chapter boundaries the shelf's three
  // editions actually split on (1–4, 5–12, 13–24 — see `src/reader/shelf.ts`).
  // This is the book `MatterPlacement`'s anchored variants exist for: a
  // "front"/"back" placement would pile all nine documents onto the two ends
  // of the catalog, putting volume one's afterword after volume three.
  //
  // Order within a boundary is the order here, and epilogue precedes afterword
  // on purpose: the epilogue is narrative (it closes the volume's story, and
  // lives in `rewrites/` beside the chapter rewrites), the afterword is the
  // editorial note about it.
  //
  // MATTER IS FREE BY DEFAULT AND PAYWALLED BY OPT-IN, and the opt-in is NOT
  // configured here. It is a per-document flag on `matterMaster.premium`,
  // toggled in the console's "Matter paywall" view, because which documents are
  // sold is a merchandising decision that changes without a code edit — unlike
  // `placement`, which is a fact about how the book is bound.
  //
  // So this list says nothing about who pays for what, and reading it as the
  // whole story is the mistake to avoid: a foreword and an epilogue sitting
  // side by side here may be free and paid respectively. `productIdForMatter`
  // derives WHICH product would sell a document from its placement; the flag
  // decides whether it is sold at all.
  //
  // (It was unconditionally free until the volume-three epilogue and afterword
  // needed locking — narrative prose closing a volume that is paid from Book 14
  // on. Volume two's epilogue is the same shape and stays free; that is a
  // merchandising choice now, not a property of matter.)
  //
  // Volume three's three documents anchor to chapters 13 and 24, which stay out
  // of the catalog until the paywalled half is seeded — `readingOrder` drops
  // matter whose anchor chapter is absent, so they are configured and seeded
  // now but appear only when their volume does.
  matter: [
    {
      file: "vol_1_foreword.md",
      slug: "vol-1-foreword",
      placement: { at: "before", chapter: 1 },
    },
    {
      file: "rewrites/vol_1_epilogue.md",
      slug: "vol-1-epilogue",
      placement: { at: "after", chapter: 4 },
    },
    {
      file: "vol_1_afterword.md",
      slug: "vol-1-afterword",
      placement: { at: "after", chapter: 4 },
    },
    {
      file: "vol_2_foreword.md",
      slug: "vol-2-foreword",
      placement: { at: "before", chapter: 5 },
    },
    {
      file: "rewrites/vol_2_epilogue.md",
      slug: "vol-2-epilogue",
      placement: { at: "after", chapter: 12 },
    },
    {
      file: "vol_2_afterword.md",
      slug: "vol-2-afterword",
      placement: { at: "after", chapter: 12 },
    },
    {
      file: "vol_3_foreword.md",
      slug: "vol-3-foreword",
      placement: { at: "before", chapter: 13 },
    },
    {
      file: "rewrites/vol_3_epilogue.md",
      slug: "vol-3-epilogue",
      placement: { at: "after", chapter: 24 },
    },
    {
      file: "vol_3_afterword.md",
      slug: "vol-3-afterword",
      placement: { at: "after", chapter: 24 },
    },
  ],
  // Three volumes, each its own sellable product. Volume one is wholly free;
  // volumes two and three each open with a free sample chapter (Book 5, Book
  // 13) and paywall the rest. An entitlement to volume two does not open
  // volume three.
  //
  // TWO OF THE FREE CHAPTERS ARE CHOSEN BY SYLLABUS, NOT BY POSITION. Books 9
  // (the cyclops) and 21 (the contest of the bow) are free because they are on
  // the ninth-grade reading list that most US classrooms teach this epic from,
  // and a free sample nobody is assigned is a sample nobody arrives at. This
  // is what makes a teacher's link to a class land on prose instead of on the
  // paywall — see `docs/plan/ela-classroom-extension.md`. Both are seeded and
  // published already, which is what makes moving them here safe: build-corpus
  // hard-errors on a FREE chapter with no retelling and silently skips an
  // unseeded premium one, so this direction of change is the asserting one.
  volumes: [
    { volumeNumber: 1, chapters: { from: 1, to: 4 } },
    {
      volumeNumber: 2,
      chapters: { from: 5, to: 12 },
      premium: {
        freeChapters: [5, 9],
        productId: "odyssey-vol2-premium",
        displayName: "The Odyssey, Volume Two",
        defaultAmountCents: DEFAULT_PREMIUM_AMOUNT_CENTS,
        currency: "usd",
      },
    },
    {
      volumeNumber: 3,
      chapters: { from: 13, to: 24 },
      premium: {
        freeChapters: [13, 21],
        productId: "odyssey-vol3-premium",
        displayName: "The Odyssey, Volume Three",
        defaultAmountCents: DEFAULT_PREMIUM_AMOUNT_CENTS,
        currency: "usd",
      },
    },
  ],
  legacyBundleProductId: "odyssey-premium",
};

/**
 * Heidi — 14 chapters, German original. Entered the codebase
 * as a validation-only book for the aligner's held-out sample (plan 11) and was
 * promoted into `BOOK_SLUGS` once it was wanted in the reader; all 14 chapters
 * parse, round-trip, and reconcile between the fixture and seed splitters.
 */
const HEIDI: BookConfig = {
  slug: "heidi",
  title: "Heidi",
  sourceLanguage: "de",
  sourceLanguageName: "German",
  sourceDir: "rewrite-template/books/heidi",
  chapters: Array.from({ length: 14 }, (_, i) => i + 1),
  retellingHeadingNoun: "Chapter",
  retellingHeadingNumeral: "arabic",
  originalHeadingNoun: "Chapter",
  // German is a modern European language: `small` scores 0.999 on the ch1
  // fixture, same as `large` — see {@link OpenAIEmbedModel}.
  openaiEmbedModel: "text-embedding-3-small",
  alignBackend: "openai", // German — direct multilingual, no translation
  original: {
    doubledHeaders: false,
    romanNumeralDisplayTitles: false,
    stripImageRefs: false,
    stripPageMarkers: false,
    verseOriginal: false,
  },
  verseChunking: false,
  matter: [],
  // One cover, so the single-volume rule: chapters 1–6 open, 7–14 paid.
  premium: {
    freeChapters: freeChaptersThrough(SINGLE_VOLUME_FREE_CHAPTERS),
    productId: "heidi-premium",
    displayName: "Heidi — the complete retelling",
    defaultAmountCents: DEFAULT_PREMIUM_AMOUNT_CENTS,
    currency: "usd",
  },
};

/**
 * Frankenstein's 28 division labels: Walton's four letters, then Shelley's own
 * Chapters 1–24 restarting at 1. Sequential chapter 11 is the source's
 * "Chapter 7" — see {@link BookConfig.divisionLabels}.
 */
const FRANKENSTEIN_DIVISIONS: readonly string[] = [
  ...Array.from({ length: 4 }, (_, i) => `Letter ${String(i + 1)}`),
  ...Array.from({ length: 24 }, (_, i) => `Chapter ${String(i + 1)}`),
];

/**
 * Frankenstein — 28 sequential divisions (4 letters + 24 chapters), English
 * original. It entered validation-only, exactly as heidi did, and now ships:
 * in `BOOK_SLUGS`, with a cover on the shelf rather than a `FORTHCOMING` tile.
 *
 * ONE edition of 28, deliberately, though `book.yaml` upstream divides it into
 * six narrative volumes. Splitting it is a later decision and may not be all
 * six, so the source repo's volume structure is left untouched and the shelf
 * simply does not use it yet. The five `rewrites/vol_N_epilogue.md` files are
 * NOT seeded as matter for the same reason Don Quixote's are not — each is a
 * verbatim crop of the next volume's opening chapter, so in a single-edition
 * reader they would print the same prose twice.
 */
const FRANKENSTEIN: BookConfig = {
  slug: "frankenstein",
  title: "Frankenstein",
  sourceLanguage: "en",
  sourceDir: "rewrite-template/books/frankenstein",
  chapters: Array.from({ length: 28 }, (_, i) => i + 1),
  // The COLLECTIVE noun ("28 chapters"); the four letters are named
  // individually by `divisionLabels`, which is what a heading uses.
  retellingHeadingNoun: "Chapter",
  retellingHeadingNumeral: "arabic",
  divisionLabels: FRANKENSTEIN_DIVISIONS,
  originalHeadingNoun: "Chapter",
  openaiEmbedModel: "text-embedding-3-small",
  alignBackend: "gateway", // English — self-hosted qwen, no translation
  original: {
    doubledHeaders: false,
    romanNumeralDisplayTitles: false,
    stripImageRefs: false,
    stripPageMarkers: false,
    verseOriginal: false,
  },
  verseChunking: false,
  matter: [],
  // ONE cover for all 28 divisions (see above), so the single-volume rule
  // applies even though the source repo divides the novel into six volumes:
  // divisions 1–6 — Walton's four letters and Shelley's first two chapters —
  // are open, 7–28 are paid.
  premium: {
    freeChapters: freeChaptersThrough(SINGLE_VOLUME_FREE_CHAPTERS),
    productId: "frankenstein-premium",
    displayName: "Frankenstein — the complete retelling",
    defaultAmountCents: DEFAULT_PREMIUM_AMOUNT_CENTS,
    currency: "usd",
  },
};

/**
 * Alice's Adventures in Wonderland — 12 chapters, English original. The
 * plainest book in the set: single volume, arabic headings in both the source
 * and the rewrite, no source-format quirks at all, and no matter documents
 * (the source repo has none authored yet).
 */
const ALICE: BookConfig = {
  slug: "alice",
  title: "Alice's Adventures in Wonderland",
  sourceLanguage: "en",
  sourceDir: "rewrite-template/books/alice",
  chapters: Array.from({ length: 12 }, (_, i) => i + 1),
  retellingHeadingNoun: "Chapter",
  retellingHeadingNumeral: "arabic",
  originalHeadingNoun: "Chapter",
  openaiEmbedModel: "text-embedding-3-small",
  alignBackend: "gateway", // English — self-hosted qwen, no translation
  original: {
    doubledHeaders: false,
    romanNumeralDisplayTitles: false,
    stripImageRefs: false,
    stripPageMarkers: false,
    verseOriginal: false,
  },
  verseChunking: false,
  matter: [],
  // One cover, so the single-volume rule: chapters 1–6 open, 7–12 paid.
  premium: {
    freeChapters: freeChaptersThrough(SINGLE_VOLUME_FREE_CHAPTERS),
    productId: "alice-premium",
    displayName: "Alice's Adventures in Wonderland — the complete retelling",
    defaultAmountCents: DEFAULT_PREMIUM_AMOUNT_CENTS,
    currency: "usd",
  },
};

/**
 * The Nutcracker and the Mouse King — 14 chapters, German original (Hoffmann's
 * 1816 "Nußknacker und Mausekönig").
 *
 * The one book whose source carries Gutenberg page-break artifacts inline, so
 * it is also the only one setting `stripPageMarkers` — see that field.
 *
 * German, so it routes like heidi: direct multilingual embedding on `small`,
 * which scores 0.999 true-window percentile on the heidi ch1 fixture and needs
 * no help from `large` (see {@link OpenAIEmbedModel}).
 */
const NUTCRACKER: BookConfig = {
  slug: "nutcracker",
  title: "The Nutcracker and the Mouse King",
  sourceLanguage: "de",
  sourceLanguageName: "German",
  sourceDir: "rewrite-template/books/nutcracker",
  chapters: Array.from({ length: 14 }, (_, i) => i + 1),
  retellingHeadingNoun: "Chapter",
  retellingHeadingNumeral: "arabic",
  originalHeadingNoun: "Chapter",
  openaiEmbedModel: "text-embedding-3-small",
  alignBackend: "openai", // German — direct multilingual, no translation
  original: {
    doubledHeaders: false,
    romanNumeralDisplayTitles: false,
    stripImageRefs: false,
    stripPageMarkers: true, // `[249]`–`[317]` left inline by the PG scan
    verseOriginal: false,
  },
  verseChunking: false,
  matter: [],
  // One cover, so the single-volume rule: chapters 1–6 open, 7–14 paid.
  premium: {
    freeChapters: freeChaptersThrough(SINGLE_VOLUME_FREE_CHAPTERS),
    productId: "nutcracker-premium",
    displayName: "The Nutcracker and the Mouse King — the complete retelling",
    defaultAmountCents: DEFAULT_PREMIUM_AMOUNT_CENTS,
    currency: "usd",
  },
};

/**
 * Don Quixote — Spanish original. All eight volumes of the source repo's
 * `book.yaml` (chapters 1–126) are configured and priceable; `chapters` still
 * stops at 27.
 *
 * THOSE TWO RANGES DIFFER ON PURPOSE, and the difference is the whole safety
 * story. `volumes` is what the PRICING side reads — it makes the complete-title
 * bundle a preorder for all seven paywalled volumes, and it marks every chapter
 * from 28 on premium so nothing in an unseeded volume can leak as free.
 * `chapters` is what the PIPELINE reads: it is what seed aligns and embeds and
 * what build-corpus emits.
 *
 * The dormancy the two scripts provide — skipping an unretold PREMIUM chapter
 * rather than erroring — only holds for a chapter with no rewrite file, and the
 * source repo has rewrites through chapter 52. So extending `chapters` to 126
 * would NOT leave volume four dormant: `bun run seed don-quixote --force` would
 * align and embed 25 more Spanish chapters, and build-corpus (which reads the
 * rewrite directly in `--local`) already emits them. They would arrive with no
 * free sample (`freeChapters: []`, correct for content that doesn't exist) and
 * no cover of their own, so `shelfUnits`' `ownerOf` — which binds by `from`
 * alone — would file all 25 under the VOLUME THREE cover, selling one product
 * over chapters another product locks.
 *
 * So shipping volume four is three edits together, not one: widen `chapters` to
 * 52, give volume four its free opening chapter, and add its cover to `SHELF`.
 * Until then this range is what holds it back, exactly as it did before volumes
 * existed. Volumes 5–8 (chapter 53 on) have no rewrites at all and are dormant
 * by both mechanisms.
 *
 * Spanish is a modern European language, so it takes `small` like German
 * rather than the `large` Homeric Greek needs (see {@link OpenAIEmbedModel}).
 */
const DON_QUIXOTE: BookConfig = {
  slug: "don-quixote",
  title: "Don Quixote",
  sourceLanguage: "es",
  sourceLanguageName: "Spanish",
  sourceDir: "rewrite-template/books/don_quixote",
  // 1–27: the three volumes that are seeded and have covers. NOT 126 — see the
  // note above; the rewrites for 28–52 exist, so widening this ships volume
  // four (into volume three's cover) rather than leaving it dormant.
  chapters: Array.from({ length: 27 }, (_, i) => i + 1),
  retellingHeadingNoun: "Chapter",
  retellingHeadingNumeral: "arabic",
  originalHeadingNoun: "Chapter",
  openaiEmbedModel: "text-embedding-3-small",
  alignBackend: "openai", // Spanish — direct multilingual, no translation
  original: {
    doubledHeaders: false,
    romanNumeralDisplayTitles: false,
    stripImageRefs: false,
    stripPageMarkers: false,
    verseOriginal: false,
  },
  verseChunking: false,
  // No matter documents. The source repo's `rewrites/vol_N_epilogue.md` files
  // are NOT candidates the way the odyssey's are: an odyssey epilogue is
  // narrative that closes its volume, while a Don Quixote epilogue is a
  // verbatim re-telling of the first pages of the NEXT volume's opening chapter
  // (vol 1's is chapter 9 almost word for word), authored as a print teaser.
  // Seeding one here would put the same prose in the catalog twice, a few taps
  // apart, in a reader that has no page-turn to justify it.
  matter: [],
  // Eight volumes, transcribed from `book.yaml` — Cervantes's own four `partes`
  // for the 1605 novel plus four seams chosen by reading for the 1615 one, in
  // sequential 1–126 numbering (Book Two's chapter N is 52+N).
  //
  // Volume one is wholly free. Volumes two and three, which are SEEDED, each
  // open with a free sample chapter (9, 15). Volumes four through eight name no
  // free chapter at all: `freeChapters: []` is the correct state for a volume
  // whose content does not exist yet — a free chapter is a promise that a
  // specific chapter is readable, and build-corpus hard-errors on a free
  // chapter with no seeded retelling while skipping premium ones silently. Each
  // gets its sample the day it is seeded, in the same edit that widens
  // `chapters` and adds its cover; until then nothing puts its chapters in the
  // catalog at all, so the volume is priceable but unreachable, and fails
  // closed.
  volumes: [
    { volumeNumber: 1, chapters: { from: 1, to: 8 } },
    {
      volumeNumber: 2,
      chapters: { from: 9, to: 14 },
      premium: {
        freeChapters: [9],
        productId: "don-quixote-vol2-premium",
        displayName: "Don Quixote, Volume Two",
        defaultAmountCents: DEFAULT_PREMIUM_AMOUNT_CENTS,
        currency: "usd",
      },
    },
    {
      volumeNumber: 3,
      chapters: { from: 15, to: 27 },
      premium: {
        freeChapters: [15],
        productId: "don-quixote-vol3-premium",
        displayName: "Don Quixote, Volume Three",
        defaultAmountCents: DEFAULT_PREMIUM_AMOUNT_CENTS,
        currency: "usd",
      },
    },
    {
      volumeNumber: 4,
      chapters: { from: 28, to: 52 },
      premium: {
        freeChapters: [],
        productId: "don-quixote-vol4-premium",
        displayName: "Don Quixote, Volume Four",
        defaultAmountCents: DEFAULT_PREMIUM_AMOUNT_CENTS,
        currency: "usd",
      },
    },
    {
      volumeNumber: 5,
      chapters: { from: 53, to: 67 },
      premium: {
        freeChapters: [],
        productId: "don-quixote-vol5-premium",
        displayName: "Don Quixote, Volume Five",
        defaultAmountCents: DEFAULT_PREMIUM_AMOUNT_CENTS,
        currency: "usd",
      },
    },
    {
      volumeNumber: 6,
      chapters: { from: 68, to: 81 },
      premium: {
        freeChapters: [],
        productId: "don-quixote-vol6-premium",
        displayName: "Don Quixote, Volume Six",
        defaultAmountCents: DEFAULT_PREMIUM_AMOUNT_CENTS,
        currency: "usd",
      },
    },
    {
      volumeNumber: 7,
      chapters: { from: 82, to: 109 },
      premium: {
        freeChapters: [],
        productId: "don-quixote-vol7-premium",
        displayName: "Don Quixote, Volume Seven",
        defaultAmountCents: DEFAULT_PREMIUM_AMOUNT_CENTS,
        currency: "usd",
      },
    },
    {
      volumeNumber: 8,
      chapters: { from: 110, to: 126 },
      premium: {
        freeChapters: [],
        productId: "don-quixote-vol8-premium",
        displayName: "Don Quixote, Volume Eight",
        defaultAmountCents: DEFAULT_PREMIUM_AMOUNT_CENTS,
        currency: "usd",
      },
    },
  ],
  legacyBundleProductId: "don-quixote-premium",
};

/**
 * Every configured book, keyed by slug. Iterate `BOOK_SLUGS` for anything
 * user-facing (it carries catalog ORDER, which this record does not); iterate
 * this when you mean "every book the parsers must handle" — the round-trip
 * test and the aligner scorer do, so a book still being validated is covered
 * before it ever ships.
 */
export const BOOK_CONFIGS: Record<string, BookConfig> = {
  [PETER_PAN.slug]: PETER_PAN,
  [ODYSSEY.slug]: ODYSSEY,
  [HEIDI.slug]: HEIDI,
  [FRANKENSTEIN.slug]: FRANKENSTEIN,
  [ALICE.slug]: ALICE,
  [DON_QUIXOTE.slug]: DON_QUIXOTE,
  [NUTCRACKER.slug]: NUTCRACKER,
};

/** Book slugs in catalog order. */
export const BOOK_SLUGS: readonly string[] = [
  PETER_PAN.slug,
  ODYSSEY.slug,
  HEIDI.slug,
  ALICE.slug,
  DON_QUIXOTE.slug,
  NUTCRACKER.slug,
  FRANKENSTEIN.slug,
];

/** Look up a book config by slug, throwing on an unknown slug. */
export function getBookConfig(slug: string): BookConfig {
  const config = BOOK_CONFIGS[slug];
  if (!config) throw new Error(`unknown book slug: ${slug}`);
  return config;
}
